import { useAccount } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useRecoilState } from 'recoil';
import { BackendClient } from '~/backend/backend-client';
import { CompositeCbctClient } from '~/backend/composite-cbct-client';
import { ContouringClient } from '~/backend/contouring-client';
import { DataClient } from '~/backend/data-client';
import { DoseClient } from '~/backend/dose-client';
import { MetricsClient } from '~/backend/metrics-client';
import { RegistrationClient } from '~/backend/registration-client';
import { TranslationClient } from '~/backend/translation-client';
import { APIType } from '~/backend/types';
import {
    TokenProvider,
    useMsalTokenProvider,
} from '~/hooks/auth/useMsalTokenProvider';
import { generateSessionId, sessionIDState } from '~/state/atoms/backendAtoms';
import AppConfig from '~/utils/appconfig';
import { transformAvailableModelsData } from '~/utils/transforms/predictionModelsDataTransforms';

/**
 * A React hook to retrieve the current session ID
 *
 * @param {api} Model type to retrieve the models for
 *
 * @returns {BackendClient} Backend client
 *
 */
export const useSessionID = () => {
    const [sessionID, setSessionID] = useRecoilState(sessionIDState);

    const resetSessionID = () => {
        const newSessionID =
            import.meta.env.VITE_STATIC_SESSION_ID ?? generateSessionId();
        setSessionID(newSessionID);

        return newSessionID;
    };

    return {
        sessionID,
        resetSessionID,
    };
};

/**
 * A React hook to retrieve the backend connector by the model type
 *
 * @param {api} Model type to retrieve the models for
 *
 * @returns {BackendClient} Backend client
 *
 */
export const useBackend = (api: APIType): BackendClient => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useMsalTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    switch (api) {
        case APIType.WORKSPACE:
            return new BackendClient(
                AppConfig.backendUrl,
                tokenProvider,
                sessionID
            );
        case APIType.CONTOURING:
            return new ContouringClient(
                AppConfig.backendUrl,
                tokenProvider,
                sessionID
            );
        case APIType.DOSE:
            return new DoseClient(
                AppConfig.backendUrl,
                tokenProvider,
                sessionID
            );
        case APIType.REGISTRATION:
            return new RegistrationClient(
                AppConfig.backendUrl,
                tokenProvider,
                sessionID
            );
        case APIType.TRANSLATION:
            return new TranslationClient(
                AppConfig.backendUrl,
                tokenProvider,
                sessionID
            );
        case APIType.DATA:
            return new ContouringClient(
                AppConfig.backendUrl,
                tokenProvider,
                sessionID
            );
        case APIType.METRICS:
            return new MetricsClient(
                AppConfig.backendUrl,
                tokenProvider,
                sessionID
            );
    }
    throw new Error('Unknown API type!');
};

/**
 * A React hook to retrieve the contouring backend connector
 *
 * @returns {ContouringClient} Backend client
 *
 */
export const useContouringBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useMsalTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });
    // useMemo to instantiate the client only when sessionID changes
    const client = useMemo(() => {
        return new ContouringClient(
            AppConfig.backendUrl,
            tokenProvider,
            sessionID
        );
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the dose backend connector
 *
 * @returns {DoseClient} Backend client
 *
 */
export const useDoseBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useMsalTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new DoseClient(AppConfig.backendUrl, tokenProvider, sessionID);
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the data backend connector
 *
 * @returns {DoseClient} Backend client
 *
 */
export const useDataBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useMsalTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new DataClient(AppConfig.backendUrl, tokenProvider, sessionID);
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the translation backend connector
 *
 * @returns {TranslationClient} Backend client
 *
 */
export const useTranslationBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useMsalTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new TranslationClient(
            AppConfig.backendUrl,
            tokenProvider,
            sessionID
        );
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the registration backend connector
 *
 * @returns {RegistrationClient} Backend client
 *
 */
export const useRegistrationBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useMsalTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new RegistrationClient(
            AppConfig.backendUrl,
            tokenProvider,
            sessionID
        );
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * React hook returning the composite-CBCT v2 backend client. Shares
 * the MSAL token scope + session id with the registration client
 * because composite CBCT lives on the same adapt+ backend surface.
 *
 * @returns {CompositeCbctClient} Backend client
 */
export const useCompositeCbctBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useMsalTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new CompositeCbctClient(
            AppConfig.backendUrl,
            tokenProvider,
            sessionID
        );
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the metrics backend connector
 *
 * @returns {MetricsClient} Backend client
 *
 */
export const useMetricsBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useMsalTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new MetricsClient(
            AppConfig.backendUrl,
            tokenProvider,
            sessionID
        );
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the user models from the backends. Requires the user to be signed in via AAD
 *
 * @param {api} Optional model type to retrieve the models for
 *
 * @returns {isPending} Boolean to show that the loading is in progress
 * @returns {string[] | undefined} errors (if any)
 * @returns {UserModels | undefined} Associated user models
 *
 */
export const useBackendModels = (api: APIType = APIType.CONTOURING) => {
    const client = useBackend(api);

    return useQuery({
        queryKey: ['models', api],
        queryFn: async () => {
            const models = await client.models();
            return transformAvailableModelsData(models);
        },
    });
};

/**
 * A React hook to retrieve the user customizations from the backends. Requires the user to be signed in via AAD
 *
 * @param {api} Optional model type to retrieve the models for
 *
 * @returns {isPending} Boolean to show that the loading is in progress
 * @returns {string[] | undefined} errors (if any)
 * @returns {UserCustomization[] | undefined} Associated user models
 *
 */
export const useModelCustomizations = (api: APIType = APIType.CONTOURING) => {
    const client = useBackend(api);
    const msalAccount = useAccount();

    return useQuery({
        queryKey: ['customizations', api],
        queryFn: async () => {
            return client.modelCustomizations(
                msalAccount?.localAccountId ?? 'unauthenticated'
            );
        },
    });
};

const generateNewBackendClient = (
    tokenProvider: TokenProvider,
    sessionID: string
) => {
    return new BackendClient(AppConfig.backendUrl, tokenProvider, sessionID);
};

/**
 * A hook to retrieve accessRights from the backend
 *
 * @returns {isPending} Boolean to show that the loading is in progress
 * @returns {string[] | undefined} errors (if any)
 * @returns {string} Access rights
 */
export const useAccessRights = (enabled: boolean = true) => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useMsalTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(
        () => generateNewBackendClient(tokenProvider, sessionID),
        [tokenProvider, sessionID]
    );

    return useQuery({
        queryKey: ['accessRight'],
        queryFn: async () => {
            return client.accessRights();
        },
        enabled,
        retry: 5,
        retryDelay: (attemptIndex) => {
            return 1000 * attemptIndex ** 2; // 1, 4, 9, 16, 25 seconds
        },
        staleTime: 1000 * 60 * 30, // keeps successful result for 30 minutes
    });
};

/**
 * A React hook to retrieve the labeling from the backend
 *
 * @returns {isPending} Boolean to show that the loading is in progress
 * @returns {string[] | undefined} errors (if any)
 * @returns {Labeling[] | undefined} Associated labeling
 *
 */
export const useLabelingBackend = (
    api: APIType = APIType.CONTOURING,
    enabled: boolean = true
) => {
    const client = useBackend(api);

    return useQuery({
        queryKey: ['labeling', api],
        enabled: enabled,
        queryFn: async () => {
            return client.labeling(api);
        },
    });
};
